import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { SymbolsMetadataContext } from "@shared/features/common/SymbolsMetadata.context";
import { sharedLib } from "@shared/services/shared.service";
import {
  ASSET_AS_PAIR_EXCHANGES,
  CONTRACTS_EXCHANGES,
  MARGIN_EXCHANGES,
} from "@shared/common/constants";

import { pickBy, uniq } from "lodash";
import { mapValues } from "lodash";
import { getAssetFromSymbol, getPairFromSymbol } from "@shared/utils/misc";
import { useSelector } from "react-redux";
import { selectSymbolsMetadata } from "@shared/slices/common.slice";
import { useClientsData, useRefValue } from "@shared/utils/hooks/misc";
import { sortByStringProperty } from "@shared/utils/general";
import { SymbolMetadata } from "@shared/types";

const SymbolsMetadataProvider = ({ children }: PropsWithChildren) => {
  const getExchangeToSymbolsStatus =
    sharedLib.sharedApi.common.useGetSymbolsMetadataQuery(undefined);
  const symbolsMetadata = useSelector(selectSymbolsMetadata);
  const clientsData = useClientsData();
  const clientsDataRef = useRefValue(clientsData);
  const { isSuccess } = getExchangeToSymbolsStatus;

  const exchangeToSortedSymbols: Record<string, string[]> | undefined =
    useMemo(() => {
      if (symbolsMetadata) {
        return mapValues(symbolsMetadata, (val) => {
          return Object.keys(val.symbols).sort();
        });
      }
    }, [symbolsMetadata]);

  const exchangeToSymbols: Record<string, Set<string>> | undefined =
    useMemo(() => {
      if (exchangeToSortedSymbols) {
        return mapValues(exchangeToSortedSymbols, (val) => new Set(val));
      }
    }, [exchangeToSortedSymbols]);

  const exchangeToActiveSymbols: Record<string, string[]> | undefined =
    useMemo(() => {
      if (symbolsMetadata) {
        return mapValues(symbolsMetadata, (val) => {
          const defaults = val.defaults;
          const validSymbols = Object.entries(val.symbols)
            .filter(([, symbolMetadata]) => {
              const merged = mergeWithDefaults(symbolMetadata, defaults);
              return merged?.is_active;
            })
            .map(([key]) => key);
          return validSymbols.sort();
        });
      }
    }, [symbolsMetadata]);

  const allSymbols = useMemo(() => {
    return uniq(Object.values(exchangeToActiveSymbols || {}).flat()).sort(
      sortByStringProperty
    );
  }, [exchangeToActiveSymbols]);

  const exchangeToActiveSymbolsSet: Record<string, Set<string>> | undefined =
    useMemo(
      () => mapValues(exchangeToActiveSymbols, (symbols) => new Set(symbols)),
      [exchangeToActiveSymbols]
    );

  const exchangeToActiveMicaSymbols: Record<string, string[]> | undefined =
    useMemo(() => {
      if (symbolsMetadata) {
        return mapValues(symbolsMetadata, (val) => {
          const defaults = val.defaults;
          const validSymbols = Object.entries(val.symbols)
            .filter(([, symbolMetadata]) => {
              const merged = mergeWithDefaults(symbolMetadata, defaults);
              return merged?.mica_is_active !== false && merged?.is_active;
            })
            .map(([key]) => key);
          return validSymbols.sort();
        });
      }
    }, [symbolsMetadata]);

  const exchangeToActiveMicaSymbolsSet:
    | Record<string, Set<string>>
    | undefined = useMemo(
    () => mapValues(exchangeToActiveMicaSymbols, (symbols) => new Set(symbols)),
    [exchangeToActiveMicaSymbols]
  );

  const getSymbolExchanges = useCallback(
    (symbol?: string) => {
      if (!symbol) return [];
      return Object.keys(
        pickBy(exchangeToSymbols, (symbolsSet) => symbolsSet.has(symbol))
      ).sort(sortByStringProperty);
    },
    [exchangeToSymbols]
  );

  const isSymbolActiveInAccount = useCallback(
    (symbol?: string, accountId?: number) => {
      const account = clientsDataRef.current?.getAccountById(accountId);
      if (!symbol || !accountId || !account) return;
      const isMicaAccount = clientsDataRef.current?.getIsMicaAccount(accountId);
      const symbolsMapping = isMicaAccount
        ? exchangeToActiveMicaSymbolsSet
        : exchangeToActiveSymbolsSet;
      return symbolsMapping?.[account.exchange]?.has(symbol);
    },
    [exchangeToActiveMicaSymbolsSet, exchangeToActiveSymbolsSet, clientsData]
  );

  const isSymbolActiveInExchange = useCallback(
    (symbol?: string, exchange?: string) => {
      if (!symbol || !exchange) return;
      return exchangeToActiveSymbolsSet?.[exchange]?.has(symbol);
    },
    [exchangeToActiveSymbolsSet]
  );

  const getSymbolMetadata = useCallback(
    (symbol?: string, exchange?: string) => {
      if (!symbol || !exchange) return;
      const exchangeData = symbolsMetadata?.[exchange];
      const symbolData = exchangeData?.symbols[symbol];
      if (!symbolData) return;
      const defaults = exchangeData.defaults;
      return mergeWithDefaults(symbolData, defaults);
    },
    [symbolsMetadata]
  );

  const getMainCoin = (symbol?: string, exchange?: string) =>
    getSymbolMetadata(symbol, exchange)?.main_coin;

  const getDisplayCoin = (symbol?: string, exchange?: string) => {
    if (!symbol || !exchange) return;
    if (CONTRACTS_EXCHANGES.has(exchange)) return getAssetFromSymbol(symbol);
    return getPairFromSymbol(symbol);
  };

  const getQuantityCoin = (
    symbol?: string,
    exchange?: string,
    isLeftSideOnly?: boolean
  ) => {
    if (exchange && CONTRACTS_EXCHANGES.has(exchange)) return "contracts";
    if (isLeftSideOnly && symbol) return getAssetFromSymbol(symbol);
    const mainCoin = getSymbolMetadata(symbol, exchange)?.main_coin;
    if (mainCoin) {
      return symbol?.replace(new RegExp("/?" + mainCoin + "/?", "gm"), "");
    }
  };

  const getExchangeConversionFormula = (
    symbol?: string,
    exchange?: string
  ): string | undefined => {
    if (!symbol || !exchange) return;
    const metadata = getSymbolMetadata(symbol, exchange);
    if (!metadata) return;
    const { contract_size, taker_fee } = metadata;
    const fee = taker_fee ?? 0;
    const isContractExchange = CONTRACTS_EXCHANGES.has(exchange);
    const isAssetAsPairNonContract =
      ASSET_AS_PAIR_EXCHANGES.has(exchange) && !isContractExchange;

    let equivalentFormula = "1 ";
    if (isContractExchange) {
      const size = contract_size ?? 1;
      equivalentFormula += `* ${size} / order_price `;
    } else if (isAssetAsPairNonContract) {
    } else {
      equivalentFormula += "* order_price ";
    }
    equivalentFormula += `* ((1 / leverage) + ${fee} + (1 / 0.9 * ${fee}))`;
    return equivalentFormula;
  };

  const getAmountConversionFormula = (
    symbol?: string,
    exchange?: string
  ): string | undefined => {
    if (!symbol || !exchange) return;
    if (!MARGIN_EXCHANGES.has(exchange)) {
      return "quantity * order_price";
    }
    const exchangeFormula = getExchangeConversionFormula(symbol, exchange);
    if (!exchangeFormula) return;
    const safeMultiplier = 1 + AMOUNT_CONVERSIONS_SAFETY_FACTOR;
    return `quantity * (${exchangeFormula} * ${safeMultiplier})`;
  };

  const getQuantityConversionFormula = (
    symbol?: string,
    exchange?: string
  ): string | undefined => {
    if (!symbol || !exchange) return;
    if (!MARGIN_EXCHANGES.has(exchange)) {
      return "amount / order_price";
    }
    const exchangeFormula = getExchangeConversionFormula(symbol, exchange);
    if (!exchangeFormula) return;
    const safeMultiplier = 1 + AMOUNT_CONVERSIONS_SAFETY_FACTOR;
    return `amount / (${exchangeFormula} * ${safeMultiplier})`;
  };

  const getExchangeSymbols = useCallback(
    (exchange?: string) => {
      return exchange && exchangeToSortedSymbols
        ? exchangeToSortedSymbols[exchange]
        : [];
    },
    [exchangeToSortedSymbols]
  );

  const getAccountActiveSymbols = useCallback(
    (clientId?: number) => {
      const account = clientsDataRef.current?.getAccountById(clientId);
      const isMicaAccount = clientsDataRef.current?.getIsMicaAccount(clientId);
      const symbolsMapping = isMicaAccount
        ? exchangeToActiveMicaSymbols
        : exchangeToActiveSymbols;
      if (!clientId || !account || !symbolsMapping) return;
      return symbolsMapping[account.exchange];
    },
    [exchangeToActiveSymbols, exchangeToActiveMicaSymbols, clientsData]
  );

  const contextValue = useMemo(
    () => ({
      allSymbols,
      symbolsMetadata,
      exchangeToSymbols,
      getSymbolExchanges,
      getExchangeToSymbolsStatus,
      isSymbolActiveInAccount,
      isSymbolActiveInExchange,
      getSymbolMetadata,
      getMainCoin,
      getDisplayCoin,
      getAmountConversionFormula,
      getQuantityConversionFormula,
      getQuantityCoin,
      getExchangeSymbols,
      getAccountActiveSymbols,
      isSuccess,
    }),
    [symbolsMetadata, getAccountActiveSymbols]
  );

  return (
    <SymbolsMetadataContext.Provider value={contextValue}>
      {children}
    </SymbolsMetadataContext.Provider>
  );
};

export default React.memo(SymbolsMetadataProvider);

const AMOUNT_CONVERSIONS_SAFETY_FACTOR = 0.05;

const mergeWithDefaults = (
  symbolData: SymbolMetadata | undefined,
  defaults: SymbolMetadata | undefined
): SymbolMetadata | undefined => {
  if (!symbolData) return defaults;
  if (!defaults) return symbolData;
  return { ...defaults, ...symbolData };
};
